import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BoxMinimalisticIcon } from "@solar-icons/react/linear/box-minimalistic";
import { useActiveShowroomSession } from "../features/showroom/ActiveShowroomSession.tsx";
import { Widget2Icon } from "@solar-icons/react/linear/widget-2";
import { LayersMinimalisticIcon } from "@solar-icons/react/linear/layers-minimalistic";
import { Bag4Icon } from "@solar-icons/react/linear/bag-4";
import { HeartIcon } from "@solar-icons/react/linear/heart";
import { Login2Icon } from "@solar-icons/react/linear/login-2";
import { PowerIcon } from "@solar-icons/react/linear/power";
import { motion, useMotionValue, useReducedMotion, useTransform, type MotionValue } from "framer-motion";
import { getDirection, t, useStoreLanguage } from "../features/store-language/i18n.ts";
import { getShopOrdersPath } from "../components/Shop/Orders/shopSession.ts";
import { PendingRing } from "../components/shared/LoadingRing.jsx";
import "./ShopBottomNav.css";

interface ShopBottomNavProps {
  position: MotionValue<number>;
  hidden: boolean;
  isLoggedIn: boolean;
  logoutPending: boolean;
  onLogout: () => Promise<void>;
}

export default function ShopBottomNav({ position, hidden, isLoggedIn, logoutPending, onLogout }: ShopBottomNavProps) {
  const language = useStoreLanguage();
  const { active: activeShowroom } = useActiveShowroomSession();
  const location = useLocation();
  const reducedMotion = useReducedMotion();
  const [selection, setSelection] = useState<string | null>(null);
  const [logoutSelected, setLogoutSelected] = useState(false);
  const navRef = useRef<HTMLElement | null>(null);
  const surfaceWidth = useMotionValue(0);
  const mounted = useRef(true);
  const busy = logoutSelected || logoutPending;
  const items = [
    { key: "orders", label: t("سفارش‌ها"), path: getShopOrdersPath(), Icon: Bag4Icon },
    { key: "categories", label: t("دسته‌بندی‌ها"), path: "/categories", Icon: LayersMinimalisticIcon },
    { key: "catalogue", label: t("کاتالوگ"), path: "/", Icon: Widget2Icon },
    { key: "favorites", label: t("علاقه‌مندی‌ها"), path: "/account/favorites", Icon: HeartIcon },
    activeShowroom && isLoggedIn
      ? { key: "showroom", label: t("نمایشگاه"), path: "/showroom/", Icon: BoxMinimalisticIcon }
      : { key: "session", label: isLoggedIn ? t("خروج") : t("ورود"), path: "/login", Icon: isLoggedIn ? PowerIcon : Login2Icon },
  ];
  const routeKey = location.pathname.startsWith("/account/orders")
    ? "orders"
    : location.pathname.startsWith("/account/favorites")
      ? "favorites"
      : location.pathname === "/categories" || location.pathname.startsWith("/categories/")
        ? "categories"
        : location.pathname === "/showroom" || location.pathname.startsWith("/showroom/")
          ? (activeShowroom ? "showroom" : null)
          : location.pathname.startsWith("/account/")
            ? null
            : "catalogue";
  const activeKey = selection || routeKey;
  // One geometric spring drives both the circle and its socket, including across shells.
  const surfacePath = useTransform([position, surfaceWidth], function (values: number[]) {
    const [x, width] = values;
    const radius = 25;
    const shoulderRadius = 9;
    const reach = Math.sqrt((radius + shoulderRadius) ** 2 - shoulderRadius ** 2);
    const tangentX = radius * reach / (radius + shoulderRadius);
    const tangentY = radius * shoulderRadius / (radius + shoulderRadius);
    // The shoulder arcs and circular socket share tangents: no sharp join at the rim.
    return `M -1 0.5 H ${x - reach}
      A ${shoulderRadius} ${shoulderRadius} 0 0 1 ${x - tangentX} ${tangentY + 0.5}
      A ${radius} ${radius} 0 0 0 ${x + tangentX} ${tangentY + 0.5}
      A ${shoulderRadius} ${shoulderRadius} 0 0 1 ${x + reach} 0.5
      H ${width + 1} V 1000 H -1 Z`;
  });

  useLayoutEffect(function () {
    const nav = navRef.current;
    if (!nav) return;
    function measurePosition() {
      const bounds = nav.getBoundingClientRect();
      const target = nav.querySelector<HTMLElement>(`[data-destination="${activeKey}"]`);
      surfaceWidth.set(bounds.width);
      if (!bounds.width || !target) return;
      const targetBounds = target.getBoundingClientRect();
      const next = targetBounds.left - bounds.left + targetBounds.width / 2;
      if (reducedMotion || position.get() === 0) position.jump(next);
      else position.set(next);
    }
    measurePosition();
    const observer = new ResizeObserver(measurePosition);
    observer.observe(nav);
    return function () { observer.disconnect(); };
  }, [activeKey, language, position, reducedMotion, surfaceWidth]);

  useEffect(function () {
    setSelection(null);
  }, [location.key, hidden]);

  useEffect(function () {
    mounted.current = true;
    return function () { mounted.current = false; };
  }, []);

  async function selectLogout() {
    if (busy) return;
    setSelection("session");
    setLogoutSelected(true);
    try {
      await onLogout();
    } finally {
      if (mounted.current) {
        setSelection(null);
        setLogoutSelected(false);
      }
    }
  }

  return (
    <nav
      ref={navRef}
      className="shop-bottom-nav mobile-only"
      aria-label={t("دسترسی سریع کاتالوگ")}
      aria-hidden={hidden ? true : undefined}
      dir={getDirection()}
    >
      <svg
        className="shop-bottom-nav__surface"
        aria-hidden="true"
        focusable="false"
      >
        {activeKey ? (
          <motion.path d={surfacePath} />
        ) : (
          <rect
            width="100%"
            height="100%"
          />
        )}
      </svg>
      <motion.span
        className="shop-bottom-nav__bead"
        style={{ left: position, visibility: activeKey ? "visible" : "hidden" }}
        aria-hidden="true"
      >
        {items.map(function ({ key, Icon }) {
          return (
            <motion.span
              key={key}
              className="shop-bottom-nav__bead-icon"
              initial={false}
              animate={{
                opacity: activeKey === key ? 1 : 0,
                scale: activeKey === key ? 1 : 0.65,
                y: activeKey === key ? 0 : 6,
                rotate: activeKey === key ? 0 : -12,
              }}
              transition={reducedMotion
                ? { duration: 0 }
                : { type: "spring", stiffness: 420, damping: 24, mass: 0.6 }}
            >
              {key === "session" && logoutPending ? (
                <PendingRing />
              ) : (
                <Icon
                  size={24}
                  strokeWidth={1.85}
                  aria-hidden="true"
                />
              )}
            </motion.span>
          );
        })}
      </motion.span>
      {items.map(function ({ key, label, path, Icon }) {
        const active = activeKey === key;
        const content = (
          <>
            <span className="shop-bottom-nav__icon" aria-hidden="true">
              <Icon
                size={24}
                strokeWidth={1.65}
                aria-hidden="true"
              />
            </span>
            <span className="shop-bottom-nav__label">{label}</span>
          </>
        );
        const className = `shop-bottom-nav__item shop-bottom-nav__item--${key}${active ? " is-active" : ""}`;
        return key === "session" && isLoggedIn ? (
          <button
            key={key}
            type="button"
            className={className}
            data-destination={key}
            onClick={selectLogout}
            disabled={busy}
            aria-label={logoutPending ? t("در حال خروج…") : t("خروج از حساب کاربری")}
            aria-busy={logoutPending}
            tabIndex={hidden ? -1 : undefined}
          >
            {content}
          </button>
        ) : (
          <Link
            key={key}
            to={path}
            className={className}
            data-destination={key}
            aria-current={routeKey === key ? "page" : undefined}
            aria-disabled={busy ? true : undefined}
            tabIndex={hidden || busy ? -1 : undefined}
            onClick={function (event) {
              if (busy) event.preventDefault();
            }}
          >
            {content}
          </Link>
        );
      })}
    </nav>
  );
}
